import { useState } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack'; // For notifications if needed

interface IPasswordProps {
  title: string;
}

const initialValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string()
      .min(6, 'Minimum 6 characters')
      .max(100, 'Maximum 100 characters')
      .required('Current password is required'),
  newPassword: Yup.string()
      .min(6, 'Minimum 6 characters')
      .max(100, 'Maximum 100 characters')
      .required('New password is required'),
  confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Please confirm your new password')
});

const Password = ({ title }: IPasswordProps) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const API_URL = import.meta.env.VITE_APP_API_URL;
  const API_PASSWORD = `${API_URL}/password`;

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      setSubmitLoading(true);
      try {
        // Send PUT request to update password
        const response = await axios.put(API_PASSWORD, {
          old_password: values.currentPassword,
          new_password: values.newPassword,
          new_password_confirmation: values.confirmPassword
        });
        console.log('Password update response:', response.data);
        enqueueSnackbar('Password updated successfully.', { variant: 'success' });
        resetForm();
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || 'An unexpected error occurred.';
        console.error(errorMessage);
        setStatus(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSubmitting(false);
        setSubmitLoading(false);
      }
    }
  });

  return (
      <div className="card pb-2.5">
        <form onSubmit={formik.handleSubmit}>
          <div className="card-header" id="password_settings">
            <h3 className="card-title">{title || 'Password'}</h3>
          </div>
          <div className="card-body grid gap-5">
            {/* Current Password */}
            <div className="grid gap-1">
              <label className="form-label">Current Password</label>
              <input
                  type="password"
                  className="input"
                  placeholder="Your current password"
                  {...formik.getFieldProps('currentPassword')}
              />
              {formik.touched.currentPassword && formik.errors.currentPassword && (
                  <span role="alert" className="text-danger text-xs">
                {formik.errors.currentPassword}
              </span>
              )}
            </div>

            {/* New Password */}
            <div className="grid gap-1">
              <label className="form-label">New Password</label>
              <input
                  type="password"
                  className="input"
                  placeholder="New password"
                  {...formik.getFieldProps('newPassword')}
              />
              {formik.touched.newPassword && formik.errors.newPassword && (
                  <span role="alert" className="text-danger text-xs">
                {formik.errors.newPassword}
              </span>
              )}
            </div>

            {/* Confirm New Password */}
            <div className="grid gap-1">
              <label className="form-label">Confirm New Password</label>
              <input
                  type="password"
                  className="input"
                  placeholder="Confirm new password"
                  {...formik.getFieldProps('confirmPassword')}
              />
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <span role="alert" className="text-danger text-xs">
                {formik.errors.confirmPassword}
              </span>
              )}
            </div>

            <div className="flex justify-end">
              <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitLoading || formik.isSubmitting}
              >
                {submitLoading ? 'Please wait...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </form>
      </div>
  );
};

export { Password };
